/**
 * Локальное хранилище устройства.
 *
 * Всё, что снято, ложится сюда ПЕРВЫМ делом и только потом уезжает на сервер.
 * На выставке сети либо нет, либо она хуже, чем нет: съёмка никогда не должна
 * ждать сервера. Файлы лежат Blob-ами прямо здесь и удаляются с устройства
 * лишь после того, как сервер подтвердил приём.
 *
 * Индексируемого SQL здесь пока нет — для съёмки он не нужен. Профессиональные
 * фильтры по 3800 камням офлайн потребуют SQLite/OPFS; интерфейс этого модуля
 * специально узкий, чтобы подменить движок, не трогая экраны.
 */
import Dexie, { type Table } from 'dexie';

/**
 * Место, откуда начинается день: выставка или здание.
 *
 * Имя типа осталось прежним — переименовать его значило бы тронуть каждый
 * экран ради слова. У здания name может быть пустым: тогда его называют
 * адресом или ориентиром, см. placeTitle в core-model.
 */
export interface LocalExhibition {
  id: string; code: string; name: string;
  kind?: 'show' | 'building';
  address?: string; landmark?: string;
  /** Валюта цен, когда поставщик её не назвал. */
  currency?: string;
  city?: string; country?: string; venue?: string;
  startsOn?: string; endsOn?: string; notes?: string;
  createdAt: string;
}

/**
 * Поставщик. Поля с суффиксом Local — то, как напечатано на визитке в родном
 * письме; без суффикса — латиница. Обе записи нужны: по латинице ищут и её
 * произносят вслух, оригинал показывают самому поставщику.
 */
/**
 * Реплика беседы. who — кто говорил: 'them' — поставщик (text — перевод на
 * язык заказчика), 'me' — заказчик (text — перевод, который прочитал
 * поставщик). lang — язык самого текста.
 */
export interface TalkLine { n: number; at: string; who: 'them' | 'me'; lang: string; text: string }

/** Находка из беседы: цена, камень, условие, договорённость, контакт. */
export interface DigestItem {
  kind: 'price' | 'stone' | 'terms' | 'agreement' | 'contact' | 'info';
  text: string;
  stone?: Record<string, string | number>;
  price?: { amount: number; currency: string | null; per: 'ct' | 'lot_ct' | 'total' };
  quote?: string;
  /** Куда уже добавлено: 'stand' или id камня. */
  applied?: string[];
}

/** Выжимка беседы — её делает сервер после разговора. */
export interface TalkDigest {
  summary: string;
  items: DigestItem[];
  tasks: Array<{ title: string; due: string | null; quote?: string }>;
}

/** Беседа через переводчика. Стенд необязателен: привязывают и потом. */
export interface LocalTalk {
  id: string;
  exhibitionId?: string;
  supplierId?: string;
  mineLang: string;
  theirsLang: string;
  lines: TalkLine[];
  startedAt: string;
  endedAt?: string;
  /** Смещение часов телефона от UTC, минуты: сроки «завтра» считаются по нему. */
  tzOffset?: number;
  digest?: TalkDigest;
  digestAt?: string;
  digestError?: string;
  /** Есть реплики, которых сервер ещё не видел. */
  dirty?: boolean;
}

/**
 * Задача. Из беседы приходит предложенной (proposed) — в список дел
 * попадает только принятое. open → done; отклонённое — dismissed.
 */
export interface LocalTask {
  id: string;
  title: string;
  dueOn?: string;
  status: 'proposed' | 'open' | 'done' | 'dismissed';
  source: 'ai' | 'manual';
  supplierId?: string;
  conversationId?: string;
  createdAt: string;
  doneAt?: string;
}

export interface LocalSupplier {
  id: string; exhibitionId: string; code: string;
  companyName: string; companyNameLocal?: string;
  contactPerson?: string; contactPersonLocal?: string;
  position?: string; positionLocal?: string;
  country?: string; city?: string; cityLocal?: string;
  address?: string; addressLocal?: string;
  booth?: string; hall?: string; website?: string; notes?: string;
  /** В здании вместо стенда и зала — этаж и офис. */
  floor?: string; office?: string;
  contacts: Array<{ id: string; kind: string; value: string }>;
  seq: number;                 // локальный счётчик камней этого поставщика
  /** «Вернуться завтра»: пока выставка идёт, эта пометка живёт на главной. */
  followUp?: boolean;
  createdAt: string;
}

/** Заход к поставщику: к нему привязываются снятые камни и заметка обо всём стенде. */
export interface LocalSession {
  id: string;
  exhibitionId: string;
  supplierId: string;
  startedAt: string;
  endedAt?: string;
}

export interface LocalStone {
  id: string;
  sessionId?: string;
  /** Для камня, рождённого разделением партии — её id. */
  parcelId?: string;
  localNo: string;
  publicNo?: string | null;    // приходит с сервера после синка
  kind: 'stone' | 'parcel';
  supplierId: string;
  exhibitionId: string;
  supplierSku?: string;
  status: string;
  /** Виден покупателям в магазине. Отдельно от статуса: торг — не витрина. */
  inCatalog?: boolean;
  /** Крутится в карусели на главной магазина. Промо без каталога не бывает. */
  promo?: boolean;
  capturedAt: string;
  createdAt: string;
}

export interface LocalFact {
  id: string;
  subjectId: string;
  attribute: string;
  valueText?: string | null;
  valueNum?: number | null;
  unit?: string | null;
  sourceType: string;
  confidence: string;
  sourceRef?: Record<string, unknown> | null;
  needsReview: boolean;
  recordedAt: string;
}

export interface LocalMedia {
  id: string;
  subjectId: string;
  role: 'photo' | 'preview' | 'audio' | 'certificate' | 'card';
  blob: Blob;
  mime: string;
  bytes: number;
  uploaded: boolean;
  serverUrl?: string;
  createdAt: string;
}

/**
 * Голосовая заметка. Комментарий бывает трёх охватов, и это не украшение:
 * «этот камень греный» относится к одному, «вся эта партия из Мозамбика» —
 * к нескольким, «поставщик готов уступить десять процентов» — ко всему заходу.
 */
export interface LocalVoice {
  id: string;
  subjectId: string;                        // камень или сессия
  subjectKind: 'stone' | 'group' | 'session';
  subjectIds?: string[];                    // для group — какие именно камни
  mediaId: string;
  /** Кадр, к которому относится сказанное. Пусто — заметка про камень целиком. */
  photoMediaId?: string;
  transcript?: string;
  status: 'pending' | 'done' | 'failed';
  durationMs?: number;
  createdAt: string;
}

/**
 * Сертификат. Живёт отдельно от фактов, потому что у него своя судьба:
 * сначала распознан, потом проверен у лаборатории. Факты он порождает
 * дважды — сперва как заявление, после проверки как сертифицированные.
 */
export interface LocalCert {
  id: string;
  stoneId: string;
  labCode?: string | null;
  labName?: string | null;
  reportNo?: string | null;
  reportDate?: string | null;
  raw?: Record<string, unknown> | null;
  uncertain?: string[];
  /** Снятый без сети отчёт читает сервер, когда доедет файл. */
  scanStatus?: 'pending' | 'done' | 'failed';
  scanError?: string | null;
  verifiedAt?: string | null;
  verifyMethod?: string | null;
  verifyUrl?: string | null;
  createdAt: string;
}

/**
 * Тег вида камня.
 *
 * Словарь покупателя: чем он помечает снятое, не набирая текст у стенда.
 * Живёт в пространстве, а не на устройстве — добавленный на одном телефоне
 * должен быть и на втором. Идентификатор выводится из самого названия,
 * поэтому два телефона, добавившие «Кунцит», получат один тег, а не два.
 */
export interface LocalTag {
  id: string;
  label: string;
  sort: number;
  hidden: boolean;
  createdAt: string;
}

export interface OutboxItem {
  opId: string;
  kind: string;
  data: unknown;
  createdAt: string;
  tries: number;
  lastError?: string;
}

class DiamondDB extends Dexie {
  exhibitions!: Table<LocalExhibition, string>;
  suppliers!: Table<LocalSupplier, string>;
  sessions!: Table<LocalSession, string>;
  stones!: Table<LocalStone, string>;
  facts!: Table<LocalFact, string>;
  media!: Table<LocalMedia, string>;
  voices!: Table<LocalVoice, string>;
  certs!: Table<LocalCert, string>;
  tags!: Table<LocalTag, string>;
  talks!: Table<LocalTalk, string>;
  tasks!: Table<LocalTask, string>;
  outbox!: Table<OutboxItem, string>;
  settings!: Table<{ key: string; value: unknown }, string>;

  constructor() {
    super('diamond');

    // Версия 1 остаётся объявленной: у кого приложение уже открыто, база
    // обновится на месте, а не упадёт при первом же запросе.
    this.version(1).stores({
      exhibitions: 'id, createdAt',
      suppliers: 'id, exhibitionId, createdAt',
      stones: 'id, supplierId, exhibitionId, status, createdAt',
      facts: 'id, subjectId, attribute',
      media: 'id, subjectId, role, uploaded',
      voices: 'id, subjectId, status',
      outbox: 'opId, createdAt',
      settings: 'key',
    });

    // Версия 2: заходы к поставщику и охват голосовой заметки.
    this.version(2).stores({
      sessions: 'id, supplierId, startedAt',
      stones: 'id, supplierId, exhibitionId, sessionId, status, createdAt',
      voices: 'id, subjectId, subjectKind, status',
    }).upgrade(async (tx) => {
      // У прежних заметок охвата не было — все они относились к камню.
      await tx.table('voices').toCollection().modify((v: LocalVoice) => {
        v.subjectKind ??= 'stone';
      });
    });

    // Версия 3: сертификаты.
    this.version(3).stores({ certs: 'id, stoneId, reportNo' });

    // Версия 4: состояние разбора отчёта — снятый без сети ждёт сервера.
    this.version(4).stores({ certs: 'id, stoneId, reportNo, scanStatus' })
      .upgrade(async (tx) => {
        await tx.table('certs').toCollection().modify((c: LocalCert) => {
          c.scanStatus ??= c.raw ? 'done' : 'pending';
        });
      });

    this.version(5).stores({ tags: 'id, sort, hidden' });

    // Версия 6: камни из разделённой партии ищутся по её id.
    this.version(6).stores({
      stones: 'id, supplierId, exhibitionId, sessionId, parcelId, status, createdAt',
    });

    // Версия 7: заметка знает свой кадр — камень доснимают и говорят про
    // конкретный снимок, а не про камень вообще.
    this.version(7).stores({
      voices: 'id, subjectId, subjectKind, status, photoMediaId',
    });

    // Версия 8: беседы через переводчика.
    this.version(8).stores({ talks: 'id, supplierId, exhibitionId, startedAt' });

    // Версия 9: задачи — из бесед и заведённые руками.
    this.version(9).stores({ tasks: 'id, status, supplierId, conversationId, dueOn' });
  }
}

export const db = new DiamondDB();

/**
 * Первоначальный словарь видов.
 *
 * Порядок не алфавитный, а по тому, как часто это встречается на выставках
 * цветного камня: сапфир и рубин лежат в каждой витрине, жемчуг и бриллиант —
 * редкие гости в этой торговле. Список правится в настройках.
 */
export const DEFAULT_TAGS = [
  'Сапфир', 'Рубин', 'Изумруд', 'Шпинель', 'Параиба', 'Падпараджа',
  'Александрит', 'Цаворит', 'Турмалин', 'Гранат', 'Аквамарин', 'Танзанит',
  'Хризоберилл', 'Топаз', 'Циркон', 'Перидот', 'Морганит', 'Опал',
  'Бриллиант', 'Жемчуг',
];

/** Ключ тега выводится из названия: одинаковые теги с разных телефонов сливаются. */
export function tagId(label: string): string {
  const norm = label.trim().toLowerCase().replace(/\s+/g, '-');
  let h = 0;
  for (const ch of norm) h = (h * 31 + ch.codePointAt(0)!) >>> 0;
  return `tag_${h.toString(36)}`;
}

/**
 * Завести словарь при первом запуске.
 *
 * Признак «уже разложено» — отметка, а не пустота таблицы: загрузка с сервера
 * опережает раскладку, и по пустоте словарь не завёлся бы никогда. Ключи у
 * тегов выводятся из названия, поэтому повторная раскладка ничего не двоит.
 */
export async function seedTags(): Promise<LocalTag[]> {
  const done = await setting<boolean>('tagsSeeded', false);
  if (done) return db.tags.orderBy('sort').toArray();

  const now = new Date().toISOString();
  const rows: LocalTag[] = DEFAULT_TAGS.map((label, i) => ({
    id: tagId(label), label, sort: (i + 1) * 10, hidden: false, createdAt: now,
  }));
  await db.tags.bulkPut(rows);

  // Словарь уезжает на сервер, иначе второй телефон получит его пустым.
  // Пометка seed говорит серверу «только если такого ещё нет»: новое
  // устройство не должно вернуть тег, который владелец уже скрыл.
  for (const row of rows) {
    await enqueue('tag.upsert', { ...row, seed: true }, `seed-${row.id}`);
  }
  await setSetting('tagsSeeded', true);
  return db.tags.orderBy('sort').toArray();
}

export async function setting<T>(key: string, fallback: T): Promise<T> {
  const row = await db.settings.get(key);
  return (row?.value as T) ?? fallback;
}

export async function setSetting(key: string, value: unknown) {
  await db.settings.put({ key, value });
}

/**
 * Стереть с устройства чужой каталог.
 *
 * Телефон один, а людей у него может быть несколько: владелец вышел, вошёл
 * другой. Ключ при этом меняется, и сервер чужого уже не отдаст — но всё
 * снятое прежним человеком остаётся лежать в этой самой базе и показывается
 * следующему. Пространства разделены на сервере; на устройстве их разделяет
 * только эта функция.
 *
 * Настройки устройства (его идентификатор и короткий код) переживают чистку:
 * они принадлежат телефону, а не человеку, и участвуют во временных номерах.
 */
export async function wipeLocal() {
  await db.transaction('rw',
    [db.exhibitions, db.suppliers, db.sessions, db.stones, db.facts,
     db.media, db.voices, db.certs, db.outbox],
    async () => {
      await Promise.all([
        db.exhibitions.clear(), db.suppliers.clear(), db.sessions.clear(),
        db.stones.clear(), db.facts.clear(), db.media.clear(),
        db.voices.clear(), db.certs.clear(), db.outbox.clear(),
      ]);
    });
  // Словарь видов принадлежит человеку: у следующего он свой.
  await db.settings.where('key').anyOf(['exhibitionId', 'sessionId', 'tagsSeeded']).delete();
}

/** Поставить операцию в очередь на отправку. Сама отправка — дело sync.ts. */
export async function enqueue(kind: string, data: unknown, opId: string) {
  await db.outbox.put({ opId, kind, data, createdAt: new Date().toISOString(), tries: 0 });
}

/** Сколько всего ждёт отправки — операции плюс неотправленные файлы. */
export async function pendingCount(): Promise<number> {
  const [ops, files] = await Promise.all([
    db.outbox.count(),
    db.media.filter((m) => !m.uploaded).count(),
  ]);
  return ops + files;
}
